import { DefaultButton, IPanelProps, Panel as FluentPanel, PanelType, Stack } from "@fluentui/react";
import React, { ReactNode } from "react";

import { PrimaryButton } from "../../inputs/button/PrimaryButton";
import { Shimmer } from "../../progress/Shimmer";
import { ErrorBoundary } from "../error-boundary/ErrorBoundary";

interface BaseAsidePanelProps extends Pick<IPanelProps, "styles"> {
    isOpen: boolean;
    headerText: string;
    children: ReactNode;
    isLightDismiss?: boolean;
    isConfirmButtonDisabled?: boolean;
    width?: string;
    isLoading?: boolean;
    onDismiss?(): void;
}

export interface AsidePanelWithActionsProps extends BaseAsidePanelProps {
    type?: "action" | "loading-action";
    onCancel?(): void;
    onSubmit(): void;
}

export interface AsidePanelWithoutActionsProps extends BaseAsidePanelProps {
    type: "readonly" | "form";
}

export type AsidePanelProps = AsidePanelWithActionsProps | AsidePanelWithoutActionsProps;

export const Aside: React.FunctionComponent<AsidePanelProps> = (props: AsidePanelProps) => {
    const hasActionButtons = props.type !== "readonly" && props.type !== "form";

    const dismiss = (): void => {
        if (props.onDismiss) {
            props.onDismiss();
        }
    };

    const cancel = (): void => {
        if ((props.type === "action" || props.type === "loading-action") && props.onCancel) {
            props.onCancel();
        }
    };

    const submit = (): void => {
        if (props.type === "action" || props.type === "loading-action") {
            props.onSubmit();
        }
    };

    const renderActionButtons = (): JSX.Element => (
        <Stack
            horizontal={true}
            horizontalAlign="end"
            styles={{
                root: { marginBottom: 10 }
            }}
        >
            <DefaultButton onClick={cancel} text="Cancel" style={{ marginRight: 8 }} />
            <PrimaryButton
                text="Submit"
                onClick={submit}
                disabled={props.isConfirmButtonDisabled}
                type={props.type === "loading-action" ? "loading" : undefined}
            />
        </Stack>
    );

    return (
        <FluentPanel
            isOpen={props.isOpen}
            type={props.width ? PanelType.custom : PanelType.smallFixedFar}
            customWidth={props.width}
            onDismiss={dismiss}
            onRenderFooterContent={hasActionButtons ? renderActionButtons : undefined}
            styles={props.styles}
            headerText={props.headerText}
            closeButtonAriaLabel="Close"
            isFooterAtBottom={true}
            isLightDismiss={props.isLightDismiss}
        >
            <ErrorBoundary>
                <Stack styles={{ root: { marginTop: 10 } }}>{props.isLoading ? getShimmer() : props.children}</Stack>
            </ErrorBoundary>
        </FluentPanel>
    );
};

const getShimmer = () => {
    return (
        <Stack styles={{ root: { paddingTop: 20 } }} tokens={{ childrenGap: "l1" }}>
            <Shimmer width="30%" />
            <Shimmer width="50%" />
            <Shimmer width="70%" />
            <Shimmer width="30%" />
            <Shimmer width="50%" />
            <Shimmer width="70%" />
            <Shimmer width="30%" />
            <Shimmer width="50%" />
            <Shimmer width="70%" />
        </Stack>
    );
};
